import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useBackupRun from '../hooks/useBackupRun';
import CloudBackupGlyph from './CloudBackupGlyph';
import { setActiveTab } from '../redux/slices/appSlice';
import { track } from '../utils/analytics';
import { backupToolLabel, completionPct } from '../utils/cloudBackup';
import { useBackupToastHidden, hideBackupToast, restoreBackupToast, useRestoreWizardOpen } from '../utils/backupToastVisibility';

const MODE_KEY = 'mgBackupToastMode'; // 'expanded' | 'minimized' — device-scoped preference
// Must match the fade-out duration in App.scss (.backup-toast--leaving).
const EXIT_MS = 200;
// Hover-intent before the pill's peek opens.
//
// Deliberately in native-tooltip territory (~0.5–1s) rather than the snappier
// 260ms this started at. The pill lives in the top-right corner, which is a
// TRANSIT zone — the pointer crosses it on the way to window controls and app
// chrome — and at 260ms merely passing through popped the panel open. The peek
// is a convenience anyway: clicking the pill expands the toast and shows
// everything, so nothing is lost by making this require a deliberate rest.
const PEEK_DELAY_MS = 700;
// Backstop for the post-minimize suppression below, in case the pointer never
// happened to be over the pill and so never fires a leave event.
const PEEK_REARM_MS = 2000;

/**
 * App-wide Cloud Backup progress toast (desktop only — inert when the backup
 * bridge is absent). Up from the moment a backup starts until its last step
 * is over, whichever tab is open — one card for the whole sequence (DJ-data
 * snapshots, then the music files), with the detail line saying which step
 * it is on. See useBackupRun for what counts as a backup. Lives top-right,
 * below the tab bar.
 *
 * Two-step dismissal, so the step you cannot undo sits behind the step you
 * can: "−" minimizes to a pulsing cloud pill (undo = click the pill), and only
 * the PILL carries a close. The expanded card has no × at all — two similar
 * 14px controls side by side made the irreversible one as easy to hit as the
 * reversible one.
 *
 * Closing hides it for the rest of THIS run; the next run brings it back, and
 * the Cloud Backup page can restore it mid-run (see backupToastVisibility).
 */
export default function BackupProgressToast() {
    const run = useBackupRun();
    const dispatch = useDispatch();
    const [mode, setMode] = useState(() => {
        try { return localStorage.getItem(MODE_KEY) === 'minimized' ? 'minimized' : 'expanded'; }
        catch (e) { return 'expanded'; }
    });
    const closedForRun = useBackupToastHidden();
    // While the restore wizard is on screen its own progress bar is the
    // primary view — the toast showing the same numbers next to it is noise.
    // The moment the wizard closes, the toast (or pill) takes over: live
    // proof the restore continues, no explanatory message needed.
    const wizardOpen = useRestoreWizardOpen();
    const wasRunning = useRef(false);

    // New run → un-dismiss.
    useEffect(() => {
        if (run.running && !wasRunning.current) restoreBackupToast();
        wasRunning.current = run.running;
    }, [run.running]);

    // Exit needs the element to still BE there, so visibility lags the real
    // state by one animation. Without it the toast simply blinked out of
    // existence the instant a run ended or the user pressed Stop — the most
    // abrupt moment in the whole flow, and the one they are most likely to be
    // looking at.
    // ONE state machine for every transition, not just show/hide.
    //
    // `desired` is what should be on screen — 'expanded' | 'minimized' | null —
    // and `shown` lags it by an exit animation. Previously only appear/disappear
    // was animated, so switching between the card and the pill cut hard: the
    // outgoing form vanished in a frame and the incoming one faded in over it.
    // Worse, both forms are a fixed-position <div> at the same anchor, so React
    // reused the DOM node and merely swapped the className — which does not
    // restart a CSS animation, hence the occasional flash instead of a fade.
    // The distinct `key` per form below is what forces a real remount.
    const shouldShow = run.running && !closedForRun && !(run.kind === 'restore' && wizardOpen);
    const desired = shouldShow ? mode : null;
    const [shown, setShown] = useState(desired);
    const [leaving, setLeaving] = useState(false);
    const [peekOpen, setPeekOpen] = useState(false);
    // A REF, not state. Pointer leave clears it and pointer enter reads it, and
    // those can land in the same React batch — with state, the enter handler
    // still saw the pre-leave value and swallowed a deliberate hover. Nothing
    // renders off this flag, so it has no business being state.
    const peekSuppressed = useRef(false);
    const peekTimer = useRef(null);
    const rearmTimer = useRef(null);

    const suppressPeek = () => {
        peekSuppressed.current = true;
        clearTimeout(rearmTimer.current);
        // Backstop: if the pointer never happened to be over the pill it will
        // never fire a leave, and the peek would stay suppressed for good.
        rearmTimer.current = setTimeout(() => { peekSuppressed.current = false; }, PEEK_REARM_MS);
    };
    useEffect(() => () => { clearTimeout(peekTimer.current); clearTimeout(rearmTimer.current); }, []);
    useEffect(() => {
        // An exit can be cancelled mid-fade: `desired` dips to null (a batch
        // boundary reads as the run going quiet, or one run ends and the next
        // starts) and comes back within EXIT_MS. The cleanup clears the timer
        // that would have reset `leaving`, so this branch must clear it too —
        // otherwise the --leaving class sticks on a live toast and its
        // fill-mode holds it at opacity 0 for the rest of the run.
        if (desired === shown) { setLeaving(false); return undefined; }
        // Nothing on screen to see off — go straight to the entrance.
        if (shown === null) { setLeaving(false); setShown(desired); return undefined; }
        setLeaving(true);
        const t = setTimeout(() => { setShown(desired); setLeaving(false); }, EXIT_MS);
        return () => clearTimeout(t);
    }, [desired, shown]);

    if (shown === null) return null;

    const setModeAndSave = (m) => {
        setMode(m);
        try { localStorage.setItem(MODE_KEY, m); } catch (e) { /* private mode */ }
    };

    // Minimizing puts the pill exactly where the pointer already is, so a
    // plain :hover rule opened the peek in the same frame the card collapsed —
    // which read as "the × did nothing, it just changed shape". The peek is
    // therefore driven by intent, not by the pointer merely being there:
    // suppressed until the pointer LEAVES once (or a backstop timer fires),
    // and opened only after a short dwell.
    const minimize = () => {
        track('backup_toast_minimized', { kind: run.kind });
        setPeekOpen(false);
        suppressPeek();
        setModeAndSave('minimized');
    };
    const openBackupTab = () => {
        track('backup_progress_line_clicked', { from: 'toast', kind: run.kind });
        dispatch(setActiveTab('backup'));
    };
    const isSnapshot = run.kind === 'snapshot';
    const isRestore = run.kind === 'restore';
    // The backup pass with no step reporting yet: the moments before the first
    // snapshot event, and the hand-offs between steps.
    const isPass = run.kind === 'pass';
    // Restore's merge/apply legs pulse like snapshots do (no ratio to draw).
    const restorePulse = isRestore && !!run.stage;
    // A snapshot is one archive, not a queue — there is no meaningful ratio to
    // draw, so it gets the tool's name and the pulsing cloud instead of a bar.
    // Restore's files leg prefers bytes: the per-file count only moves on each
    // completion, so a count-based bar sits still and then jumps.
    const pct = isSnapshot || isPass || restorePulse
        ? null
        : isRestore && run.bytesTotal > 0
            ? Math.min(100, Math.round(((run.bytesReceived || 0) / run.bytesTotal) * 100))
            : completionPct(run.done, run.total);
    const fileName = !isSnapshot && !isPass && !restorePulse && run.currentFile
        ? String(run.currentFile).split(/[\\/]/).pop()
        : null;
    const RESTORE_STAGE_LABELS = {
        checking: 'Checking what’s already here',
        downloading: 'Downloading your backup',
        decompressing: 'Preparing',
        applying: 'Applying',
        relinking: 'Pointing your DJ app at the files',
        merging: 'Updating your DJ apps',
        writing: 'Updating your DJ apps',
    };
    // Files-engine stages with nothing better to name — the count-only event
    // that opens a run, and the negotiate round-trips between batches. When a
    // file is in hand, its name is the detail instead.
    const FILES_STAGE_LABELS = {
        preparing: 'Preparing…',
        hashing: 'Preparing…',
        checking: 'Checking what’s already backed up…',
        uploading: 'Uploading…',
    };
    // One headline for the whole backup. It used to change per step ("Backing
    // up Traktor", then "Backing up") and read as several toasts in a row;
    // the step goes in the detail line, where a change reads as progress.
    const headline = isRestore ? 'Restoring' : 'Backing up';
    const snapshotLabel = run.tool === 'model' ? 'Portable copy' : backupToolLabel(run.tool || '') || 'DJ app data';
    const detail = isRestore
        ? (restorePulse ? (RESTORE_STAGE_LABELS[run.stage] || 'Restoring') : null)
        : isPass
            ? (run.stage === 'between' ? 'Checking what’s left…' : 'Getting ready…')
            : isSnapshot
                // Until bytes move a snapshot is a change CHECK — export,
                // hash, compare — and "uploading" would claim a re-upload
                // that may never happen.
                ? `${snapshotLabel} · ${run.stage === 'uploading' ? 'uploading' : 'checking for changes'}`
                : (!fileName ? (FILES_STAGE_LABELS[run.stage] || null) : null);
    const glyphVariant = isRestore ? 'down' : 'up';
    const countLine = isRestore
        ? (run.total > 0 ? `${run.done.toLocaleString()} of ${run.total.toLocaleString()} files` : 'Restoring')
        : (run.total > 0 ? `${run.done.toLocaleString()} of ${run.total.toLocaleString()} tracks` : 'Backing up');

    if (shown === 'minimized') {
        return (
            <div
                key="pill"
                className={`backup-toast-pill${leaving ? ' backup-toast--leaving' : ''}${peekOpen ? ' backup-toast-pill--peeking' : ''}`}
                onPointerEnter={() => {
                    if (peekSuppressed.current) return;
                    clearTimeout(peekTimer.current);
                    peekTimer.current = setTimeout(() => setPeekOpen(true), PEEK_DELAY_MS);
                }}
                onPointerLeave={() => {
                    clearTimeout(peekTimer.current);
                    clearTimeout(rearmTimer.current);
                    setPeekOpen(false);
                    // The pointer has been away; a deliberate hover may open it now.
                    peekSuppressed.current = false;
                }}
            >
                {/* Minimized, the cloud says a backup is happening but nothing
                    about how it is going — which is the one thing you minimize
                    it wanting to check on later. Hover brings the numbers back
                    without having to expand the card. */}
                <div className="backup-toast-peek" role="status" aria-hidden={!peekOpen}>
                    <span className="backup-toast-peek-count">
                        {isSnapshot || isPass || restorePulse ? headline : countLine}
                    </span>
                    {pct != null && (
                        <span className="backup-toast-peek-bar"><span style={{ width: `${pct}%` }} /></span>
                    )}
                    {fileName && <span className="backup-toast-peek-file">{fileName}</span>}
                    {run.failed > 0 && (
                        <span className="backup-toast-peek-failed">{`${run.failed.toLocaleString()} couldn’t upload`}</span>
                    )}
                </div>
                <button
                    type="button"
                    className="backup-toast-pill-main"
                    // No `title` here: a native tooltip would race the peek
                    // panel and both would show. The peek says what clicking
                    // does instead, which is also where it's useful.
                    aria-label={isRestore ? 'Restoring — open Cloud Backup progress' : 'Backing up — open Cloud Backup progress'}
                    // Keyboard focus only. A mouse click focuses too — and
                    // focus fires on mousedown, before click on mouseup — so
                    // opening the peek here flashed the hover panel for the
                    // press duration on every click-to-expand. :focus-visible
                    // is true for keyboard focus, which is who this is for.
                    onFocus={(e) => {
                        if (peekSuppressed.current) return;
                        if (!e.target.matches(':focus-visible')) return;
                        setPeekOpen(true);
                    }}
                    onBlur={() => setPeekOpen(false)}
                    onClick={() => {
                        track('backup_toast_expanded', { kind: run.kind });
                        // Close the peek FIRST. It is anchored left of the pill,
                        // exactly where the expanding card is about to appear —
                        // left open it stayed lit through the whole fade and the
                        // card grew out over the top of it, which is the flash.
                        clearTimeout(peekTimer.current);
                        setPeekOpen(false);
                        setModeAndSave('expanded');
                    }}
                >
                    {/* Minimized, this glyph is the entire message — it has to
                        be readable as a cloud at a glance, not a dot. */}
                    <CloudBackupGlyph variant={glyphVariant} size={26} className="backup-pulse" />
                </button>
                <button
                    type="button"
                    className="backup-toast-pill-close"
                    title={isRestore ? 'Hide until the next restore' : 'Hide until the next backup'}
                    onClick={() => { track('backup_toast_closed', { from: 'pill', kind: run.kind }); hideBackupToast(); }}
                >×</button>
            </div>
        );
    }

    return (
        <div key="card" className={`backup-toast${leaving ? ' backup-toast--leaving' : ''}`}>
            <div className="backup-toast-head">
                <button type="button" className="backup-toast-title" onClick={openBackupTab} title="Open Cloud Backup">
                    <CloudBackupGlyph variant={glyphVariant} size={18} className="backup-pulse" />
                    <span>{headline}</span>
                    {!isSnapshot && !isPass && !restorePulse && run.total > 0 && (
                        <span className="backup-toast-count">{countLine}</span>
                    )}
                </button>
                {/* Reads as ×, behaves as minimize. The two-step dismissal is
                    unchanged — this collapses the card to the cloud pill, and
                    only the PILL's × actually hides it — but a dash was too
                    quiet to find, and × is where the eye goes on a card like
                    this. */}
                <button
                    type="button"
                    className="backup-toast-btn"
                    title="Minimize to a cloud"
                    aria-label="Minimize to a cloud"
                    onClick={minimize}
                >×</button>
            </div>
            {/* Real progress only — no indeterminate sweep. Until the engine
                reports library totals the bar simply isn't shown. */}
            {pct != null && (
                <div className="backup-toast-bar">
                    <span style={{ width: `${pct}%` }} />
                </div>
            )}
            {fileName && <div className="backup-toast-file" title={run.currentFile}>{fileName}</div>}
            {detail && <div className="backup-toast-file">{detail}</div>}
            {/* Without this the count above looks frozen: it is the number of
                files that are SAFE, and it cannot move while every upload is
                failing. Say so rather than letting it read as a stuck UI. */}
            {run.failed > 0 && (
                <div className="backup-toast-failed">
                    {`${run.failed.toLocaleString()} couldn’t upload`}
                </div>
            )}
            {/* Restores get their Stop right here — this toast is the ONLY
                surface for an auto-resumed run (deliberately no separate
                announcement card), and the user who quit-as-stop'd last
                session must not have to hunt through Settings to say no
                again. Files leg only: the merge legs are seconds long and
                restoreStop doesn't govern them. */}
            {isRestore && !restorePulse && (
                <div className="backup-toast-actions">
                    <button
                        type="button"
                        className="backup-toast-stop"
                        onClick={() => {
                            track('backup_restore_stopped', { from: 'progress_toast' });
                            const api = window.electron?.backup;
                            api?.restoreStop?.().catch?.(() => {});
                            // A stopped restore must not silently resurrect on
                            // the next launch — clearing the markers makes the
                            // stop as durable as the wizard's.
                            api?.setEnabled?.({
                                restorePending: false, restorePendingScope: null,
                                restoreAutoAttempts: 0, restorePromptDismissed: true,
                            }).catch?.(() => {});
                        }}
                    >
                        Stop
                    </button>
                </div>
            )}
        </div>
    );
}
